import { useNavigate } from '@tanstack/react-router'
import { useEffect } from 'react'

import { useSession } from '@/features/_hooks/use-session'
import type { AuthSession } from '@/features/_lib/auth-client.types'
import { useCompanyStore } from '@/features/_stores'

interface WithAuthParams {
  allowedFor: string[]
}

export interface WithAuthComponentProps {
  session: AuthSession
}

export const WithAuth = <P extends object>(
  Component: React.ComponentType<P & WithAuthComponentProps>,
  { allowedFor }: WithAuthParams,
) => {
  const WrappedComponent = (props: P) => {
    const { data: session, status } = useSession()
    const navigate = useNavigate()

    useEffect(() => {
      if (status === 'unauthenticated') {
        navigate({ to: '/login' })
      }

      if (status === 'authenticated' && session && !allowedFor.includes(session.user.userType)) {
        const companySlug = useCompanyStore.getState().company?.slug
        if (session.user.userType === 'tenant' && companySlug) {
          navigate({ to: `/companies/${companySlug}/applications` })
        } else {
          navigate({ to: '/applications' })
        }
      }
    }, [status, session, navigate])

    if (status !== 'authenticated' || !session) {
      return null
    }

    return <Component {...props} session={session} />
  }

  WrappedComponent.displayName = `WithAuth(${Component.displayName || Component.name || 'Component'})`

  return WrappedComponent
}
